"use client";

import React, {useLayoutEffect, useRef, useState} from "react";
import {cn} from "@/shared/lib/utils";
import {HorizontalTabProps} from "./HorizontalTab.types";
import {
    tabButtonStyles,
    tabIndicatorInnerStyles,
    tabIndicatorStyles,
    tabLabelStyles,
    tabListStyles,
} from "./HorizontalTab.styles";
import {Button} from "@/shared/components/ui/button";

export const HorizontalTab = ({
                                  tabs,
                                  activeTab,
                                  onTabChange,
                                  className,
                                  size = "md",
                                  variant = "default",
                                  indicatorProgress,
                                  swipeable = false,
                              }: HorizontalTabProps) => {
    const [underlineScale, setUnderlineScale] = useState(1);
    const [dragIndicatorProgress, setDragIndicatorProgress] = useState<number | undefined>(undefined);
    const dragStartXRef = useRef(0);
    const dragStartYRef = useRef(0);
    const dragStartIndexRef = useRef(0);
    const dragActiveRef = useRef(false);
    const dragAxisRef = useRef<"x" | "y" | null>(null);
    const suppressNextClickRef = useRef(false);
    const tabListRef = useRef<HTMLUListElement | null>(null);

    useLayoutEffect(() => {
        setUnderlineScale(1);
        const frame = requestAnimationFrame(() => setUnderlineScale(0.5));
        return () => cancelAnimationFrame(frame);
    }, [activeTab]);

    React.useEffect(() => {
        // Po zmianie aktywnego taba zawsze wracamy z podglądu drag do stanu stabilnego.
        setDragIndicatorProgress(undefined);
    }, [activeTab]);

    const tabCount = tabs?.length ?? 0;
    const activeIndex = tabs?.findIndex((tab) => tab.id === activeTab) ?? -1;
    const indicatorWidth = tabCount > 0 ? 100 / tabCount : 0;
    const resolvedIndicatorProgress = dragIndicatorProgress ?? indicatorProgress ?? activeIndex;

    const resetDragState = () => {
        dragActiveRef.current = false;
        dragAxisRef.current = null;
        setDragIndicatorProgress(undefined);
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLUListElement>) => {
        if (!swipeable || e.button !== 0 || tabCount < 2) return;
        dragActiveRef.current = true;
        dragAxisRef.current = null;
        dragStartXRef.current = e.clientX;
        dragStartYRef.current = e.clientY;
        dragStartIndexRef.current = Math.max(0, activeIndex);
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLUListElement>) => {
        if (!swipeable || !dragActiveRef.current) return;

        const rawDeltaX = e.clientX - dragStartXRef.current;
        const deltaY = e.clientY - dragStartYRef.current;
        const absX = Math.abs(rawDeltaX);
        const absY = Math.abs(deltaY);

        if (dragAxisRef.current === null) {
            if (absX > 6 || absY > 6) {
                if (absX >= absY) {
                    dragAxisRef.current = "x";
                    e.currentTarget.setPointerCapture(e.pointerId);
                } else {
                    dragAxisRef.current = "y";
                    dragActiveRef.current = false;
                    setDragIndicatorProgress(undefined);
                    return;
                }
            } else {
                return;
            }
        }

        if (dragAxisRef.current === "y") {
            setDragIndicatorProgress(undefined);
            return;
        }

        const listWidth = tabListRef.current?.offsetWidth ?? 1;
        const tabWidth = listWidth / tabCount;
        const isAtFirstTab = dragStartIndexRef.current === 0;
        const isAtLastTab = dragStartIndexRef.current === tabCount - 1;
        const draggingOutOfBounds =
            (isAtFirstTab && rawDeltaX < 0) || (isAtLastTab && rawDeltaX > 0);
        const effectiveDeltaX = draggingOutOfBounds ? 0 : rawDeltaX;
        const progress = dragStartIndexRef.current + (effectiveDeltaX / tabWidth);
        setDragIndicatorProgress(Math.min(tabCount - 1, Math.max(0, progress)));
    };

    const handlePointerEnd = (e: React.PointerEvent<HTMLUListElement>) => {
        if (!swipeable || !dragActiveRef.current) return;

        if (dragAxisRef.current === "x") {
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
            }
        }

        const deltaX = e.clientX - dragStartXRef.current;
        const listWidth = tabListRef.current?.offsetWidth ?? 1;
        const tabWidth = listWidth / tabCount;
        const didSwipe = dragAxisRef.current === "x" && Math.abs(deltaX) >= Math.max(22, tabWidth * 0.16);

        if (didSwipe) {
            // Gesture direction follows the tab switch direction:
            // drag left -> previous tab, drag right -> next tab.
            const direction = deltaX < 0 ? -1 : 1;
            const targetIndex = Math.min(tabCount - 1, Math.max(0, dragStartIndexRef.current + direction));
            const targetTab = tabs[targetIndex];
            if (targetTab && !targetTab.disabled && targetTab.id !== activeTab) {
                onTabChange(targetTab.id);
            }
        }

        suppressNextClickRef.current = dragAxisRef.current === "x";
        resetDragState();
    };

    const handlePointerLeave = () => {
        // Z pointer capture nie musimy resetować stanu przy wyjściu,
        // bo zdarzenia są nadal dostarczane do elementu.
        // Jeśli chcemy przerwać przy wyjściu, to tylko gdy nie ma aktywnego przeciągania (np. hover).
        if (!dragActiveRef.current) {
            resetDragState();
        }
    };

    const handleClickCapture = (e: React.MouseEvent<HTMLUListElement>) => {
        if (!suppressNextClickRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        suppressNextClickRef.current = false;
    };

    if (tabCount === 0) {
        return (
            <div className={cn("text-sm text-muted-foreground", className)}>
                Brak dostępnych zakładek
            </div>
        );
    }

    return (
        <nav className={cn("w-full", className)}>
            <ul
                ref={tabListRef}
                className={tabListStyles({variant})}
                style={{touchAction: swipeable ? "pan-y" : undefined}}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerEnd}
                onPointerCancel={handlePointerEnd}
                onPointerLeave={handlePointerLeave}
                onClickCapture={handleClickCapture}
            >
                {tabs.map((tab) => {
                    const isActive = tab.id === activeTab;
                    return (
                        <li key={tab.id} className="flex-1">
                            <Button
                                variant="ghost"
                                type="button"
                                id={`tab-${tab.id}`}
                                disabled={tab.disabled}
                                className={cn("h-auto rounded-none shadow-none", tabButtonStyles({
                                    isActive,
                                    size,
                                    variant
                                }))}
                                onClick={() => {
                                    if (!isActive && !tab.disabled) {
                                        onTabChange(tab.id);
                                    }
                                }}
                            >
                                {tab.icon}
                                <span className={tabLabelStyles()}>{tab.label}</span>
                            </Button>
                        </li>
                    );
                })}
                {activeIndex >= 0 && indicatorWidth > 0 && (
                    <span
                        className={tabIndicatorStyles({variant})}
                        style={{
                            width: `${indicatorWidth}%`,
                            transform: `translateX(${resolvedIndicatorProgress * 100}%)`,
                            transition: dragIndicatorProgress !== undefined || indicatorProgress !== undefined ? "none" : undefined,
                        }}
                    >
                        <span
                            className={tabIndicatorInnerStyles({variant})}
                            style={{
                                transform: `scaleX(${underlineScale})`,
                            }}
                        />
                    </span>
                )}
            </ul>
        </nav>
    );
};

